/**
 * Order Service
 *
 * This service provides a unified API for order operations,
 * handling the differences between Demo and FHIR/Greenfield modes.
 */

import { getConfig, getApiEndpoint } from '../config/environmentConfig';
import { getApiUrl, apiUtils, isRunningLocally } from '../utils/apiUtils';
import { transformOrderFromBackend } from '../utils/fieldMapping';
import { filterMockOrders } from '../utils/orderFilters';

/**
 * Normalizes order data regardless of source
 * @param {Object} order - Raw order data
 * @param {string} source - Data source ('demo' or 'greenfield')
 * @returns {Object} - Normalized order data
 */
function normalizeOrderData(order, source = 'demo') {
  if (!order) return null;

  // Clone to avoid mutations
  const normalizedOrder = { ...order };

  // Add source identifier if not present
  normalizedOrder._source = normalizedOrder._source || source;

  return normalizedOrder;
}

function extractOrderPayload(response) {
  if (!response) return null;
  if (response.success === false) {
    const message =
      response.error?.message || response.error || response.message || 'Order request failed';
    throw new Error(message);
  }

  const payload = response.data ?? response;
  if (payload && payload.success === false) {
    const message =
      payload.error?.message || payload.error || payload.message || 'Order request failed';
    throw new Error(message);
  }

  return payload?.data ?? payload?.order ?? payload;
}

/**
 * Fetches orders for a patient
 * @param {string} patientId - The patient ID
 * @returns {Promise<Array>} - Array of normalized orders
 */
export async function getOrders(patientId) {
  // getOrders call logging removed

  const config = getConfig();
  let endpoint;

  if (config.mode === 'greenfield') {
    endpoint = `${getApiEndpoint('orders')}?patient=${patientId}`;
  } else {
    // In demo mode, use the centralized API utility
    endpoint = getApiUrl(`patients/${patientId}/orders`);
  }

  // Fetching orders endpoint logging removed

  try {
    const res = await apiUtils.get(endpoint);

    if (!res?.success) {
      return [];
    }

    let orders = [];
    const payload = res.data;

    if (config.mode === 'greenfield') {
      if (payload?.entry && Array.isArray(payload.entry)) {
        orders = payload.entry
          .filter(entry => entry.resource && entry.resource.resourceType === 'ServiceRequest')
          .map(entry => entry.resource);
      }
    } else {
      orders =
        (Array.isArray(payload?.data) && payload.data) ||
        payload?.orders ||
        payload?.data?.orders ||
        (Array.isArray(payload) ? payload : []);
    }

    const transformedOrders = transformOrderFromBackend(orders || []);
    const normalizedOrders = transformedOrders.map(order => normalizeOrderData(order, config.mode));
    const filteredOrders = filterMockOrders(normalizedOrders, {
      allowMock: Boolean(config.mockDataEnabled),
      hideManual: isRunningLocally(),
      allowRecent: true,
      recentThresholdMs: 10 * 60 * 1000,
    });

    return filteredOrders;
  } catch (error) {
    if (error.response?.status !== 404) {
      console.error(`[OrderService] Error fetching orders for patient ${patientId}:`, error);
      console.error('[OrderService] Error response:', error.response?.data);
      console.error('[OrderService] Error status:', error.response?.status);
    }
    return [];
  }
}


/**
 * Creates a new order
 * @param {string} patientId - The patient ID
 * @param {Object} orderData - The order data
 * @returns {Promise<Object>} - The created order
 */
export async function createOrder(patientId, orderData) {
  // createOrder call logging removed

  const config = getConfig();
  let endpoint;
  let payload;

  if (config.mode === 'greenfield') {
    endpoint = getApiEndpoint('orders');
    // Transform to FHIR ServiceRequest if needed
    if (!orderData.resourceType) {
      payload = {
        resourceType: 'ServiceRequest',
        status: 'active',
        intent: 'order',
        subject: {
          reference: `Patient/${patientId}`,
        },
        code: {
          text: orderData.name || orderData.description || 'Unknown Order',
        },
        ...orderData,
      };
    } else {
      payload = orderData;
    }
  } else {
    // In demo/hospital mode, use the worker endpoint with expected camelCase payload
    endpoint = '/api/orders';

    const orderName =
      orderData.orderName || orderData.name || orderData.description || 'Order';
    const urgency = orderData.urgency || orderData.priority || 'routine';

    payload = {
      orderName,
      testName: orderName,
      description: orderData.description || orderData.name || '',
      notes: orderData.instructions || orderData.rationale || orderData.description || '',
      category: orderData.category || orderData.type || 'other',
      orderType: orderData.orderType || orderData.type || 'Lab',
      instructions: orderData.instructions || orderData.rationale || '',
      priority: urgency,
      urgency,
      orderedBy: orderData.orderedBy || 'Provider',
      source: orderData.source || 'ui',
    };
  }

  // POST request and payload logging removed

  try {
    // Use apiUtils instead of axios to handle authentication
    const response = await apiUtils.post(endpoint, { ...payload, patientId });
    const orderPayload = extractOrderPayload(response);
    // Order creation success logging removed
    // Transform response back to frontend format
    const transformedOrder = transformOrderFromBackend(orderPayload);
    const normalizedOrder = normalizeOrderData(transformedOrder, config.mode);
    // Normalized order logging removed
    return normalizedOrder;
  } catch (error) {
    console.error(`[OrderService] Error creating order for patient ${patientId}:`, error);
    console.error('[OrderService] Error response:', error.response?.data);
    console.error('[OrderService] Error status:', error.response?.status);
    throw error;
  }
}

/**
 * Updates an existing order
 * @param {string} patientId - The patient ID
 * @param {string} orderId - The order ID
 * @param {Object} orderData - The updated order data
 * @returns {Promise<Object>} - The updated order
 */
export async function updateOrder(patientId, orderId, orderData) {
  const config = getConfig();
  let endpoint;

  if (config.mode === 'greenfield') {
    endpoint = `${getApiEndpoint('orders')}/${orderId}`;
  } else {
    // In demo/hospital mode, prefer the worker's explicit status endpoint when updating status
    if (orderData && Object.prototype.hasOwnProperty.call(orderData, 'status')) {
      endpoint = `/api/orders/${orderId}/status`;
    } else {
      // Fallback to generic endpoint (may not be implemented on worker)
      endpoint = `/api/orders/${orderId}`;
    }
  }

  try {
    // Shape request body based on endpoint
    let body = {};
    if (endpoint.endsWith('/status')) {
      // Worker expects { status, notes? }
      body = { status: orderData?.status };
      if (orderData?.instructions) body.notes = orderData.instructions;
    } else {
      // Only send fields the backend understands (best-effort)
      const allowed = ['status', 'priority', 'instructions', 'orderedBy'];
      body = Object.fromEntries(
        Object.entries(orderData || {}).filter(([k, v]) => allowed.includes(k) && v !== undefined)
      );
    }

    const response = await apiUtils.put(endpoint, body);
    const orderPayload = extractOrderPayload(response);
    const transformedOrder = transformOrderFromBackend(orderPayload);
    return normalizeOrderData(transformedOrder, config.mode);
  } catch (error) {
    console.error(`[OrderService] Error updating order ${orderId}:`, error);
    throw error;
  }
}

/**
 * Creates an order from a FHIR ServiceRequest (from AI recommendation)
 * @param {string} patientId - The patient ID
 * @param {Object} serviceRequest - FHIR ServiceRequest object
 * @returns {Promise<Object>} - The created order
 */
export async function createOrderFromServiceRequest(patientId, serviceRequest) {
  if (!serviceRequest || serviceRequest.resourceType !== 'ServiceRequest') {
    throw new Error('Invalid ServiceRequest object');
  }

  const config = getConfig();

  // Extract LOINC/CPT codes from the ServiceRequest
  const loincCode = serviceRequest.code?.coding?.find(c => c.system?.includes('loinc'))?.code;
  const cptCode = serviceRequest.code?.coding?.find(c => c.system?.includes('cpt'))?.code;
  const snomedCode = serviceRequest.code?.coding?.find(c => c.system?.includes('snomed'))?.code;

  // Build order payload with full FHIR metadata
  const orderPayload = {
    patientId,
    testName: serviceRequest.code?.text || 'Order',
    orderName: serviceRequest.code?.text || 'Order',
    description: serviceRequest.note?.[0]?.text || serviceRequest.code?.text || '',
    orderType: serviceRequest.category?.[0]?.coding?.[0]?.display || 'Other',
    priority: serviceRequest.priority || 'routine',
    urgency: serviceRequest.priority || 'routine',
    notes: serviceRequest.note?.[0]?.text || '',
    status: 'pending',
    source: 'ai_recommendation',
    loincCode,
    cptCode,
    snomedCode,
    // Store FHIR extensions for protocol source
    protocolSource: serviceRequest.extension?.find(e => e.url?.includes('protocol-source'))?.valueString,
    protocolId: serviceRequest.extension?.find(e => e.url?.includes('protocol-id'))?.valueString,
    // Include full FHIR ServiceRequest for downstream interoperability export
    fhirServiceRequest: serviceRequest,
  };

  // Use the orders endpoint
  const endpoint = '/api/orders';

  try {
    const response = await apiUtils.post(endpoint, orderPayload);
    const orderPayloadData = extractOrderPayload(response);
    const transformedOrder = transformOrderFromBackend(orderPayloadData);
    return normalizeOrderData(transformedOrder, config.mode);
  } catch (error) {
    console.error(`[OrderService] Error creating order from ServiceRequest for patient ${patientId}:`, error);
    throw error;
  }
}

/**
 * Deletes an order
 * @param {string} patientId - The patient ID
 * @param {string} orderId - The order ID
 * @returns {Promise<void>}
 */
export async function deleteOrder(patientId, orderId) {
  const config = getConfig();

  let endpoint;
  if (config.mode === 'greenfield') {
    endpoint = `${getApiEndpoint('orders')}/${orderId}`;
  } else {
    // In demo/hospital mode, delete via worker orders endpoint
    endpoint = `/api/orders/${orderId}`;
  }

  try {
    const response = await apiUtils.delete(endpoint);
    if (response?.success === false || response?.data?.success === false) {
      const message =
        response?.error?.message ||
        response?.error ||
        response?.data?.error?.message ||
        response?.data?.error ||
        'Failed to delete order';
      throw new Error(message);
    }
  } catch (error) {
    console.error(
      `[OrderService] Error deleting order ${orderId} for patient ${patientId}:`,
      error
    );
    throw error;
  }
}

export default {
  getOrders,
  createOrder,
  createOrderFromServiceRequest,
  updateOrder,
  deleteOrder,
};
